/** @format */

import {METHOD_NET30, CART_COMPLETE_STATUS} from 'frontier-constants';
import {parseEntityError} from 'frontier-form';
import update from 'immutability-helper';
import SuperAgent from 'superagent';
import React from 'react';
import {
    CartCheckoutContextDefault,
    CartCheckoutContext,
    SHIPPING_STEP,
    BILLING_STEP,
    PAYMENT_STEP
} from 'frontier/cart/components/cart/cart_checkout_context';

export const withCartHandlerResponseToState = ({
    cart,
    states,
    countries,
    activeAddresses: addresses,
    combinedCount,
    isOverCreditLimit,
    isOverPastDue
}) => {
    const {orders, shipments, ...cartWithoutOrders} = cart;
    if (cartWithoutOrders.billing_method_id == METHOD_NET30) {
        if (isOverCreditLimit || isOverPastDue) {
            cartWithoutOrders.billing_method_id = '';
        }
    }
    return {
        cart: cartWithoutOrders,
        shipments,
        isOverCreditLimit,
        combinedCount,
        isOverPastDue,
        countries,
        addresses,
        orders,
        states
    };
};

export default function withCartHandler(ComponentContainer) {
    return class extends React.Component {
        // set the inital state
        state = CartCheckoutContextDefault;

        /**
         * This is what we send to the server on save
         */
        stateToRequestBody = () => {
            const {cart} = this.state;
            return {
                payment_profile_id: cart.payment_profile_id ? cart.payment_profile_id : null,
                billing_account_last_four: cart.billing_account_last_four,
                billing_account_type_id: cart.billing_account_type_id,
                billing_address_id: cart.billing_address_id,
                is_blind_shipping: cart.is_blind_shipping,
                billing_method_id: cart.billing_method_id,
                discount_id: cart.discount_id,
                po_number: cart.po_number
            };
        };

        /**
         * parse the response.body to our state
         * @param body
         */
        responseBodyToState = withCartHandlerResponseToState;

        componentDidMount() {
            // decide whether to show terms or not
            const dontShowTerms = localStorage.getItem('dontShowTerms-2');

            // if the user has opted to not show the terms and conditions, set the agreement as true
            if (dontShowTerms) {
                this.setState({showTerms: false});
                this.setState({agreedToTerms: true});
            }
            SuperAgent.get('/carts/current')
                .set('accept', 'application/json')
                .end((err, response) => {
                    if (err || !response.body) {
                        window.location = '/cart';
                    } else {
                        const state = this.responseBodyToState(response.body);
                        state.loading = false;
                        this.setState(state);
                    }
                });
        }

        componentDidUpdate(prevProps, prevState, snapshot) {
            // watches for errors in the shipping card to scroll to the error if the user is too far
            // down the page
            const cardWithErrors = document.querySelector('[data-error="shipping"]'); // Check for error element
            if (cardWithErrors) cardWithErrors?.scrollIntoView({behavior: 'smooth'}); // Scroll to card smoothly
        }

        handleChange = updateTarget => {
            updateTarget.saving = {$set: true};
            updateTarget.error = {$set: false};
            if (typeof this.purchaseRequest === 'undefined') {
                if (typeof this.saveRequest != 'undefined') {
                    this.saveRequest.abort();
                }
                if (typeof this.saveTimeout != 'undefined') {
                    clearTimeout(this.saveTimeout);
                }
                this.setState(
                    state => update(state, updateTarget),
                    () => {
                        this.saveTimeout = setTimeout(this.save.bind(this, false), 500);
                    }
                );
            }
        };

        handleUpdateWithoutSave = updateTarget => {
            this.setState(state => update(state, updateTarget));
        };

        handlePurchase = () => {
            if (!(this.state.cart.billing_method_id >= 10)) {
                this.setState({
                    validationErrors: {
                        ...this.state.validationErrors,
                        payment: 'Please select a payment method to proceed.'
                    }
                });
                return;
            }

            if (!this.state.agreedToTerms) {
                this.setState({
                    validationErrors: {
                        ...this.state.validationErrors,
                        payment: 'You must agree to the terms and conditions to proceed.'
                    }
                });
                return;
            }

            this.setState({purchasing: true, error: false}, this.purchase);
        };

        /**
         * Send a final request with no data to initiate purchase.
         * Purchase will charge the payment_profile_id and update the cart status
         */
        purchase = () => {
            if (typeof this.purchaseRequest != 'undefined') {
                this.purchaseRequest.abort();
            }
            this.purchaseRequest = SuperAgent.post(`/carts/purchase/${this.state.cart.id}`)
                .set('Accept', 'application/json')
                .end((err, response) => {
                    if (err || !response.body) {
                        this.setState({
                            error: this.responseToError(err, response.body),
                            purchasing: false,
                            saving: false
                        });
                    } else {
                        if (response.body.status_id == CART_COMPLETE_STATUS) {
                            window.location =
                                '/cart_confirmation?new_checkout=true&is_label_order=1&id=' +
                                response.body.id;
                        } else {
                            this.setState({
                                error: 'I did not get a valid cart purchase response. Please contact us',
                                purchasing: false,
                                saving: false
                            });
                        }
                    }
                });
        };

        /**
         * General utility for saving any new information set for a cart such as choosing a shipping
         * method id and payment information
         */
        save = () => {
            if (typeof this.saveRequest != 'undefined') {
                this.saveRequest.abort();
            }

            this.saveRequest = SuperAgent.post('/carts/current')
                .set('Accept', 'application/json')
                .send(this.stateToRequestBody());

            this.saveRequest.end((err, response) => {
                if (err || !response.body) {
                    this.setState({
                        error: this.responseToError(err, response.body),
                        purchasing: false,
                        saving: false
                    });
                } else {
                    const state = this.responseBodyToState(response.body);
                    state.error = false;
                    state.purchasing = false;
                    state.saving = false;

                    this.setState(state);
                }
            });
        };

        handleDeleteAddress = id => {
            this.setState({deleting: true, error: false}, () => {
                SuperAgent.delete('/account/addresses/toggle-enabled/' + id)
                    .set('Accept', 'application/json')
                    .end((err, response) => {
                        if (err || !response.body) {
                            this.setState({
                                error: response.body ? response.body : err,
                                deleting: false
                            });
                        } else {
                            this.setState(state =>
                                update(state, {
                                    editing: {$set: false},
                                    deleting: {$set: false},
                                    cart: {
                                        shipping_method_code: {$set: null},
                                        shipping_method_name: {$set: null},
                                        shipping_address_id: {$set: null},
                                        shipping_method_key: {$set: null},
                                        billing_address_id: {$set: null}
                                    }
                                })
                            );
                        }
                    });
            });
        };
        /**
         * Set a default address
         */
        handleSetDefaultAddress = addressId => {
            const existingAddress = this.state.addresses.find(address => address.id == addressId);
            const updateTarget = {
                addresses: {
                    $apply: addresses => {
                        return addresses.map(address => {
                            if (address.id === addressId) {
                                address.is_default_address = true;
                            } else {
                                if (
                                    address.is_shipping_address ===
                                    existingAddress.is_shipping_address
                                ) {
                                    address.is_default_address = false;
                                }
                            }
                            return address;
                        });
                    }
                }
            };

            this.setState(state => update(state, updateTarget));

            SuperAgent.post('/account/addresses/set-address-default/' + addressId)
                .set('accept', 'application/json')
                .end((err, response) => {
                    // this.handleGetAddresses();
                });
        };

        // since we do not send any request to the server when moving from one step to another,
        // we have to validate that all the data necessary for the present step is present
        // before moving to the next
        handleSetCurrentStep = step => {
            const validationErrorMessages = {
                shipping_method_code: 'Please select a shipping method',
                address_id: 'Please select an address'
            };

            // users is going from shipping to billing
            if (step === BILLING_STEP) {
                const requiredData = ['shipping_method_code', 'address_id'];

                const missingProperties = {};

                for (const field of requiredData) {
                    this.state.shipments.forEach(shipment => {
                        if (!shipment[field]) {
                            missingProperties[shipment.id] = {
                                [field]: validationErrorMessages[field]
                            };
                        }
                    });
                }

                if (Object.keys(missingProperties).length > 0) {
                    this.setState({
                        validationErrors: {
                            ...this.state.validationErrors,
                            shipping: missingProperties
                        }
                    });
                    return;
                }
            }

            // user is going from billing to payment
            if (step === PAYMENT_STEP) {
                if (!this.state.cart.billing_address_id) {
                    this.setState({
                        validationErrors: {
                            ...this.state.validationErrors,
                            billing: {
                                billing_address_id: 'Please select a billing address'
                            }
                        }
                    });
                    return;
                }
            }

            this.setState({currentStep: step});
        };

        handleIsStepComplete = stepId => {
            if (stepId === SHIPPING_STEP) {
                const isShippingStepComplete =
                    this.state.shipments.filter(
                        ({
                            shipping_account_number_fedex,
                            shipping_method_code,
                            address_id,
                            use_customer_account_number,
                            customer_account_number
                        }) => {
                            return (
                                shipping_method_code === '' ||
                                isNaN(parseInt(address_id)) ||
                                (use_customer_account_number &&
                                    (customer_account_number === '' ||
                                        customer_account_number === null) &&
                                    (shipping_account_number_fedex === '' ||
                                        shipping_account_number_fedex === null))
                            );
                        }
                    ).length === 0;

                return isShippingStepComplete;
            } else if (stepId === BILLING_STEP) {
                return this.state.cart.billing_address_id > 0;
            }

            return false;
        };

        responseToError = (err, responseBody) => {
            let error = '';
            if (responseBody) {
                error = parseEntityError(responseBody);
            } else {
                error = err;
            }
            return error;
        };

        handleDontShowTermsAgain = value => {
            if (value) {
                localStorage.setItem('dontShowTerms-2', 'true');
                this.setState({showTerms: false});
            } else {
                localStorage.removeItem('dontShowTerms-2');
                this.setState({showTerms: true});
            }
        };

        render() {
            const props = {...this.props};

            props.onPurchase = this.handlePurchase.bind(this);
            props.onChange = this.handleChange.bind(this);
            props.onUpdateWithoutSave = this.handleUpdateWithoutSave.bind(this);
            props.onSave = this.save.bind(this);
            props.onDeleteAddress = this.handleDeleteAddress.bind(this);
            props.onSetDefaultAddress = this.handleSetDefaultAddress.bind(this);
            props.onSetCurrentStep = this.handleSetCurrentStep.bind(this);
            props.isStepComplete = this.handleIsStepComplete.bind(this);
            props.onDontShowTermsAgain = this.handleDontShowTermsAgain.bind(this);

            const ctx = {...this.state, ...props};

            return (
                <CartCheckoutContext.Provider value={ctx}>
                    <ComponentContainer />
                </CartCheckoutContext.Provider>
            );
        }
    };
}
